import type {
  ConversationalFormOption,
  ConversationalFormStep,
} from '@karehero/models';

// The v2 benefits calculator question set, in the order the designs ask it.
// Question and option ids are the care profile's, so answers save as they are;
// see api/domain/assessment/QUESTION_SET_V2.md for what each one feeds.
//
// TODO(ENG-3820): the bus pass disability types question and the closing
// "anything else" turn are talked or typed, so are added with that work.

/** The option ids chosen for each question answered, by question id. */
export type BenefitsCalculatorAnswers = Record<string, string[]>;

export interface BenefitsCalculatorOption extends ConversationalFormOption {
  /** Offered only when the answers so far call for it. Omit to always offer. */
  isOffered?: (answers: BenefitsCalculatorAnswers) => boolean;
}

export interface BenefitsCalculatorStep extends ConversationalFormStep {
  options?: BenefitsCalculatorOption[];
  /** Asked only when the answers so far call for it. Omit to always ask. */
  isAsked?: (answers: BenefitsCalculatorAnswers) => boolean;
}

/** Stands in for the care recipient's first name in each ask. */
export const CARE_RECIPIENT_PLACEHOLDER = '{careRecipient}';

const YES = 'true';
const NO = 'false';
/** Answers nothing: the question is left unanswered. */
const NOT_SURE = 'not-sure';

const YES_NO: BenefitsCalculatorOption[] = [
  { id: YES, label: 'Yes' },
  { id: NO, label: 'No' },
];

const YES_NO_NOT_SURE: BenefitsCalculatorOption[] = [
  ...YES_NO,
  { id: NOT_SURE, label: 'Not sure' },
];

export const LOCATION_ID = 'care-recipient-location';
export const AGE_BRACKET_ID = 'care-recipient-age-bracket';
export const ALREADY_RECEIVING_ID = 'care-recipient-already-receiving-benefits';
export const HAS_UNPAID_CARER_ID = 'care-recipient-has-unpaid-carer';
export const HAS_PARTNER_ID = 'care-recipient-has-partner';
export const IS_DISABLED_ID = 'care-recipient-is-disabled';
export const HELP_TIME_ID = 'care-recipient-needs-help-time-of-day';
export const SAVINGS_ID = 'care-recipient-savings-bracket';
export const CONDITION_6_MONTHS_ID =
  'care-recipient-condition-duration-6-months';

export const SCOTLAND = 'care-recipient-location-scotland';
export const UNDER_18 = 'care-recipient-age-bracket-under-18';
export const STATE_PENSION_AGE_OR_OVER =
  'care-recipient-age-bracket-state-pension-age-or-over';
export const NEEDS_NO_HELP = 'care-recipient-needs-help-none';

const isAnswered = (
  answers: BenefitsCalculatorAnswers,
  questionID: string,
  optionID: string,
) => answers[questionID]?.includes(optionID) ?? false;

const isInScotland = (answers: BenefitsCalculatorAnswers) =>
  isAnswered(answers, LOCATION_ID, SCOTLAND);

/** England, Wales or Northern Ireland: answered, and neither Scotland nor abroad. */
const isInRestOfUK = (answers: BenefitsCalculatorAnswers) =>
  [
    'care-recipient-location-england',
    'care-recipient-location-wales',
    'care-recipient-location-northern-ireland',
  ].some((id) => isAnswered(answers, LOCATION_ID, id));

const isReceiving = (benefit: string) => (answers: BenefitsCalculatorAnswers) =>
  isAnswered(
    answers,
    ALREADY_RECEIVING_ID,
    `care-recipient-already-receiving-${benefit}`,
  );

const hasUnpaidCarer = (answers: BenefitsCalculatorAnswers) =>
  isAnswered(answers, HAS_UNPAID_CARER_ID, YES);

const hasPartner = (answers: BenefitsCalculatorAnswers) =>
  isAnswered(answers, HAS_PARTNER_ID, YES);

const isSingle = (answers: BenefitsCalculatorAnswers) =>
  isAnswered(answers, HAS_PARTNER_ID, NO);

const isDisabled = (answers: BenefitsCalculatorAnswers) =>
  isAnswered(answers, IS_DISABLED_ID, YES);

const question = (
  step: Omit<BenefitsCalculatorStep, 'presentation' | 'mode'>,
): BenefitsCalculatorStep => ({
  presentation: 'chat',
  mode: 'choose',
  ...step,
});

/** Which part of a benefit they get, with options prefixed by the question id. */
const partOptions = (
  questionID: string,
  parts: [string, string][],
): BenefitsCalculatorOption[] => [
  ...parts.map(([part, label]) => ({ id: `${questionID}-${part}`, label })),
  { id: `${questionID}-not-sure`, label: 'Not sure' },
];

export const BENEFITS_CALCULATOR_STEPS: BenefitsCalculatorStep[] = [
  question({
    id: LOCATION_ID,
    ask: 'Where does {careRecipient} live?',
    options: [
      { id: 'care-recipient-location-england', label: 'England' },
      { id: SCOTLAND, label: 'Scotland' },
      { id: 'care-recipient-location-wales', label: 'Wales' },
      {
        id: 'care-recipient-location-northern-ireland',
        label: 'Northern Ireland',
      },
      { id: 'care-recipient-location-other', label: 'Outside the UK' },
    ],
  }),
  question({
    id: AGE_BRACKET_ID,
    ask: 'How old is {careRecipient}?',
    // Labels follow State Pension age (StatePensionAge in the API); ids do not.
    options: [
      { id: UNDER_18, label: 'Under 18' },
      { id: 'care-recipient-age-bracket-18-to-59', label: '18 to 59' },
      {
        id: 'care-recipient-age-bracket-60-to-state-pension-age',
        label: '60 to 65',
      },
      { id: STATE_PENSION_AGE_OR_OVER, label: '66 or over' },
    ],
  }),
  question({
    id: HELP_TIME_ID,
    ask: 'Does {careRecipient} need help from someone through the day or night?',
    options: [
      { id: NEEDS_NO_HELP, label: 'No' },
      { id: 'care-recipient-needs-help-during-day', label: 'In the day' },
      { id: 'care-recipient-needs-help-during-night', label: 'At night' },
      { id: 'care-recipient-needs-help-during-both', label: 'Day and night' },
    ],
  }),
  question({
    id: 'care-recipient-in-nursing-home',
    ask: 'Is {careRecipient} living in a care or nursing home?',
    options: YES_NO,
    isAsked: isInRestOfUK,
  }),
  question({
    id: 'care-recipient-needs-personal-care',
    ask: 'Does {careRecipient} need help washing, dressing or eating?',
    options: YES_NO,
    isAsked: isInScotland,
  }),
  question({
    id: 'care-recipient-needs-nursing-care',
    ask: 'Does {careRecipient} need nursing care?',
    options: YES_NO,
    isAsked: isInScotland,
  }),
  question({
    id: ALREADY_RECEIVING_ID,
    ask: 'Does {careRecipient} already get any of these?',
    isMultiSelect: true,
    options: [
      {
        id: 'care-recipient-already-receiving-attendance-allowance',
        label: 'Attendance Allowance',
      },
      { id: 'care-recipient-already-receiving-pip', label: 'PIP' },
      { id: 'care-recipient-already-receiving-dla', label: 'DLA' },
      {
        id: 'care-recipient-already-receiving-adp',
        label: 'Adult Disability Payment',
        isOffered: isInScotland,
      },
      {
        id: 'care-recipient-already-receiving-pension-age-disability-payment',
        label: 'Pension Age Disability Payment',
        isOffered: isInScotland,
      },
      {
        id: 'care-recipient-already-receiving-none',
        label: 'None of these',
        isExclusive: true,
      },
      {
        id: 'care-recipient-already-receiving-not-sure',
        label: 'Not sure',
        isExclusive: true,
      },
    ],
  }),
  question({
    id: 'care-recipient-pip-part',
    ask: 'Which part of PIP does {careRecipient} get?',
    options: partOptions('care-recipient-pip-part', [
      ['daily-living', 'Daily living'],
      ['mobility', 'Mobility'],
      ['both', 'Both'],
    ]),
    isAsked: isReceiving('pip'),
  }),
  question({
    id: 'care-recipient-dla-parts',
    ask: 'Which parts of DLA does {careRecipient} get?',
    options: partOptions('care-recipient-dla-parts', [
      ['care', 'Care, at the middle or highest rate'],
      ['mobility', 'Mobility, at the higher rate'],
      ['both', 'Both'],
      ['neither', 'Neither of those'],
    ]),
    isAsked: isReceiving('dla'),
  }),
  question({
    id: 'care-recipient-adp-part',
    ask: 'Which part of Adult Disability Payment does {careRecipient} get?',
    options: partOptions('care-recipient-adp-part', [
      ['daily-living', 'Daily living'],
      ['mobility', 'Mobility'],
      ['both', 'Both'],
    ]),
    isAsked: isReceiving('adp'),
  }),
  question({
    id: HAS_UNPAID_CARER_ID,
    ask: 'Are you (or someone) caring for {careRecipient} unpaid?',
    options: YES_NO,
  }),
  question({
    id: 'care-recipient-unpaid-carer-35-hours-or-more',
    ask: 'Is that 35 hours a week or more?',
    options: YES_NO,
    isAsked: hasUnpaidCarer,
  }),
  question({
    id: 'care-recipient-unpaid-carer-earns-under-limit',
    ask: 'Does the carer earn under £204 a week?',
    options: YES_NO,
    isAsked: hasUnpaidCarer,
  }),
  question({
    id: HAS_PARTNER_ID,
    ask: 'Does {careRecipient} have a partner or spouse?',
    options: YES_NO,
  }),
  question({
    id: 'care-recipient-partner-over-state-pension-age',
    ask: 'Is their partner also 66 or over?',
    options: YES_NO,
    isAsked: (answers) =>
      hasPartner(answers) &&
      isAnswered(answers, AGE_BRACKET_ID, STATE_PENSION_AGE_OR_OVER),
  }),
  question({
    id: SAVINGS_ID,
    ask: 'Roughly, how much does {careRecipient} have in savings?',
    options: [
      {
        id: 'care-recipient-savings-bracket-under-16000',
        label: 'Under £16,000',
      },
      {
        id: 'care-recipient-savings-bracket-16000-to-23250',
        label: '£16,000 to £23,250',
      },
      {
        id: 'care-recipient-savings-bracket-over-23250',
        label: 'Over £23,250',
      },
    ],
  }),
  question({
    id: 'care-recipient-joint-weekly-income-bracket',
    ask: 'Roughly, their combined weekly income?',
    options: [
      {
        id: 'care-recipient-joint-weekly-income-bracket-under-364',
        label: 'Under £364',
      },
      {
        id: 'care-recipient-joint-weekly-income-bracket-364-to-450',
        label: '£364 to £450',
      },
      {
        id: 'care-recipient-joint-weekly-income-bracket-over-450',
        label: 'Over £450',
      },
    ],
    isAsked: hasPartner,
  }),
  question({
    id: 'care-recipient-weekly-income-bracket',
    ask: "Roughly, {careRecipient}'s weekly income?",
    options: [
      {
        id: 'care-recipient-weekly-income-bracket-under-238',
        label: 'Under £238',
      },
      {
        id: 'care-recipient-weekly-income-bracket-238-to-300',
        label: '£238 to £300',
      },
      {
        id: 'care-recipient-weekly-income-bracket-over-300',
        label: 'Over £300',
      },
    ],
    isAsked: isSingle,
  }),
  // The condition questions come last as a group, out of the run of quick taps.
  question({
    id: IS_DISABLED_ID,
    ask: 'Does {careRecipient} have a disability or long-term condition?',
    options: YES_NO,
  }),
  question({
    id: CONDITION_6_MONTHS_ID,
    ask: 'Has {careRecipient} had it for six months or more?',
    options: YES_NO_NOT_SURE,
    isAsked: isDisabled,
  }),
  question({
    id: 'care-recipient-condition-duration-12-months-expected',
    ask: 'Is it likely to last a year or more?',
    options: YES_NO_NOT_SURE,
    isAsked: (answers) => isAnswered(answers, CONDITION_6_MONTHS_ID, NO),
  }),
  question({
    id: 'care-recipient-is-terminally-ill',
    ask: 'Does {careRecipient} have a terminal illness?',
    options: YES_NO,
  }),
  question({
    id: 'care-recipient-home-requires-adaptations',
    ask: "Does {careRecipient}'s home need adapting, like ramps, a wet room or rails?",
    options: YES_NO,
    isAsked: isDisabled,
  }),
];

/**
 * The steps asked for these answers, in order, each with the options it
 * offers. Answers to a step no longer asked are ignored, so a changed answer
 * cannot leave a stale one in play.
 */
export const askedSteps = (
  answers: BenefitsCalculatorAnswers,
  steps: BenefitsCalculatorStep[] = BENEFITS_CALCULATOR_STEPS,
): BenefitsCalculatorStep[] => {
  const live: BenefitsCalculatorAnswers = {};
  const asked: BenefitsCalculatorStep[] = [];
  for (const step of steps) {
    if (step.isAsked && !step.isAsked(live)) continue;
    asked.push({
      ...step,
      options: step.options?.filter(
        (option) => !option.isOffered || option.isOffered(live),
      ),
    });
    if (answers[step.id]) live[step.id] = answers[step.id];
  }
  return asked;
};
